import { Scanner } from '@yudiel/react-qr-scanner';
import { useState, useEffect } from 'react';
import { getFirestore, collection, addDoc, query, where, getDocs, orderBy, Timestamp, updateDoc, doc } from 'firebase/firestore';
import { Modal, Form, Row, Col, InputGroup, Spinner } from "react-bootstrap";
import { toast } from 'react-toastify';
import { 
    FaSortAlphaDown, FaRegClock, FaUserFriends, FaInfoCircle, 
    FaTimesCircle, FaCheckCircle, FaBan, FaFilter, 
    FaDownload, FaUserPlus, FaSearch 
} from 'react-icons/fa';

import './xDocList.css';

// --- DEVELOPER CONFIGURATION ---
const ADMIN_REQUIRED = false;
const ADMIN_PASSCODE = process.env.REACT_APP_ADMIN_PASSCODE || "1234";
// -------------------------------

const QRWebAndNativeScan = () => {
  const [scannedResult, setScannedResult] = useState('');
  const [isPaused, setIsPaused] = useState(false);

  // Modals
  const [showModal, setShowModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showGuestModal, setShowGuestModal] = useState(false);
  const [modalContent, setModalContent] = useState({ title: '', name: '', time: '', type: '', variant: 'success', subtext: '' });
  const [selectedLog, setSelectedLog] = useState(null);

  // Guest Logic State
  const [guestNameInput, setGuestNameInput] = useState('');
  const [addingGuest, setAddingGuest] = useState(false);
  const [removingGuestId, setRemovingGuestId] = useState(null); // Row-level spinner
  const [lastMemberName, setLastMemberName] = useState('');
  const [lastMemberEmail, setLastMemberEmail] = useState('');
  const [guestItems, setGuestItems] = useState([]);

  // List & Search State
  const [recentLogs, setRecentLogs] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loadingLogs, setLoadingLogs] = useState(false);

  // Sort Configuration
  const [sortConfig, setSortConfig] = useState({ key: 'scannedAt', direction: 'desc' });

  // View States
  const [manualMode, setManualMode] = useState(false);
  const [historyMode, setHistoryMode] = useState(false);
  const [isAdminAuth, setIsAdminAuth] = useState(true);

  // Date States
  const todayStr = new Date().toISOString().split('T')[0];
  const [startDate, setStartDate] = useState(todayStr);
  const [endDate, setEndDate] = useState(todayStr);

  const [passcodeAttempt, setPasscodeAttempt] = useState('');
  const [manualInput, setManualInput] = useState('');
  const [loading, setLoading] = useState(false);

  const db = getFirestore();

  const formatDisplayValue = (str) => {
    if (!str) return "";
    if (str.includes('@')) return str.toLowerCase();
    return str
      .toLowerCase()
      .split(' ')
      .map(w => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');
  };

  // --- SORTING LOGIC ---
  const toggleSort = (key) => {
    let direction = 'desc';
    if (sortConfig.key === key) {
        direction = sortConfig.direction === 'desc' ? 'asc' : 'desc';
    } else {
        direction = (key === 'fullName' || key === 'entryType') ? 'asc' : 'desc';
    }
    setSortConfig({ key, direction });
  };

  const filteredLogs = recentLogs
    .filter(log => log.fullName.toLowerCase().includes(searchTerm.toLowerCase()))
    .sort((a, b) => {
        let valA = a[sortConfig.key];
        let valB = b[sortConfig.key];
        if (sortConfig.key === 'scannedAt') {
            valA = a.scannedAt?.seconds || 0;
            valB = b.scannedAt?.seconds || 0;
        } else if (typeof valA === 'string') {
            valA = valA.toLowerCase();
            valB = valB.toLowerCase();
        }
        if (valA < valB) return sortConfig.direction === 'asc' ? -1 : 1;
        if (valA > valB) return sortConfig.direction === 'asc' ? 1 : -1;
        return (b.scannedAt?.seconds || 0) - (a.scannedAt?.seconds || 0);
    });

  // --- DATA FETCHING ---
  const fetchLogs = async (isTodayOnly = true) => {
    setLoadingLogs(true);
    try {
      let q;
      const logsRef = collection(db, "license_logs");
      if (isTodayOnly) {
        const startOfToday = new Date();
        startOfToday.setHours(0, 0, 0, 0);
        q = query(logsRef, where("scannedAt", ">=", Timestamp.fromDate(startOfToday)), orderBy("scannedAt", "desc"));
      } else {
        const [sYear, sMonth, sDay] = startDate.split('-').map(Number);
        const [eYear, eMonth, eDay] = endDate.split('-').map(Number);
        const start = new Date(sYear, sMonth - 1, sDay, 0, 0, 0);
        const end = new Date(eYear, eMonth - 1, eDay, 23, 59, 59);
        q = query(logsRef, where("scannedAt", ">=", Timestamp.fromDate(start)), where("scannedAt", "<=", Timestamp.fromDate(end)), orderBy("scannedAt", "desc"));
      }
      const querySnapshot = await getDocs(q);
      setRecentLogs(querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    } catch (err) { console.error("Firestore Error:", err); }
    finally { setLoadingLogs(false); }
  };

  useEffect(() => { if (isAdminAuth) fetchLogs(!historyMode); }, [isAdminAuth, historyMode]);

  const fetchGuestsForMember = async (email) => {
    if (!email) return;
    const logsRef = collection(db, "license_logs");
    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);
    const q = query(
      logsRef,
      where("signedInBy", "==", email.toLowerCase()),
      where("entryType", "==", "Guest"),
      where("scannedAt", ">=", Timestamp.fromDate(startOfToday)),
      orderBy("scannedAt", "desc")
    );
    const snap = await getDocs(q);
    const guests = snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(g => g.status !== 'cancelled');
    setGuestItems(guests);
  };

  const downloadCSV = () => {
    if (filteredLogs.length === 0) return toast.info("No data to download");
    const headers = ["Name", "Entry Type", "Method", "Sponsor", "Status", "Date", "Check-In Time", "Cancellation Time", "Raw Payload"];
    const rows = filteredLogs.map(log => {
      const dateObj = log.scannedAt?.toDate ? log.scannedAt.toDate() : new Date();
      const cancelDateObj = log.cancelledAt?.toDate ? log.cancelledAt.toDate() : null;
      let statusStr = log.status === 'cancelled' ? "Cancelled" : (log.isExpired ? "Denied (Expired)" : "Success");
      return [`"${log.fullName}"`,`"${log.entryType}"`,`"${log.method}"`,`"${log.signedInBy || ''}"`,`"${statusStr}"`,`"${dateObj.toLocaleDateString()}"`,`"${dateObj.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}"`,`"${cancelDateObj ? cancelDateObj.toLocaleString() : 'N/A'}"`,`"${log.rawPayload?.replace(/"/g, '""') || ''}"`];
    });
    const csvContent = [headers, ...rows].map(e => e.join(",")).join("\n");
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url); 
    link.setAttribute("download", `Access_Logs_${startDate}.csv`);
    document.body.appendChild(link); 
    link.click(); 
    document.body.removeChild(link);
  };

  const handleLockStation = () => { setIsAdminAuth(false); toast.info("Station Locked"); };

  const handleCloseModal = () => {
    setShowModal(false); setScannedResult(null); setGuestNameInput('');
    setLastMemberName(''); setLastMemberEmail(''); setIsPaused(false);
  };

  async function logVisit(name, method, type, raw = "", sponsor = "", expired = false) {
    const timestamp = new Date();
    try {
      await addDoc(collection(db, "license_logs"), { fullName: name, status: 'active', entryType: type, method: method, rawPayload: raw, signedInBy: sponsor, isExpired: expired, scannedAt: timestamp });
      fetchLogs(!historyMode);
      return timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch (err) { return new Date().toLocaleTimeString(); }
  }

  const handleQuickGuestAdd = async () => {
    if (!guestNameInput.trim()) return toast.error("Enter guest name");
    setAddingGuest(true);
    try {
      await logVisit(guestNameInput.trim(), "Quick Add", "Guest", lastMemberEmail, lastMemberEmail);
      toast.success(`Guest added: ${formatDisplayValue(guestNameInput)}`);
      setGuestNameInput('');
      if (lastMemberEmail) fetchGuestsForMember(lastMemberEmail);
    } finally { setAddingGuest(false); }
  };

  const handleCancelGuest = async (guestId, email) => {
    if (!window.confirm("Cancel this guest check-in?")) return;
    setRemovingGuestId(guestId);
    try {
      await updateDoc(doc(db, "license_logs", guestId), { status: 'cancelled', cancelledAt: Timestamp.now() });
      toast.info("Guest check-in cancelled");
      await fetchGuestsForMember(email);
      await fetchLogs(!historyMode);
    } catch (e) { toast.error("Update failed"); }
    finally { setRemovingGuestId(null); }
  };

  async function verifyMemberProfile(identifier) {
    const cleaned = identifier.trim().toLowerCase();
    const isEmail = /\S+@\S+\.\S+/.test(cleaned);
    const field = isEmail ? 'email' : 'name';
    const q = query(collection(db, "profiles"), where(field, "==", isEmail ? cleaned : identifier.trim()));
    const snap = await getDocs(q);
    if (!snap.empty) {
      const data = snap.docs[0].data();
      let isExp = false; let dateS = "N/A";
      if (data.exp) {
        const expD = data.exp.toDate ? data.exp.toDate() : new Date(data.exp);
        dateS = expD.toLocaleDateString();
        if (expD < new Date()) isExp = true;
      }
      return { name: data.name, expired: isExp, expiryDate: dateS, email: data.email };
    }
    return null;
  }

  const handlePasscodeSubmit = () => {
    if (passcodeAttempt === ADMIN_PASSCODE) { setIsAdminAuth(true); setPasscodeAttempt(''); toast.success("Station Unlocked"); }
    else { toast.error("Invalid Passcode"); setPasscodeAttempt(''); }
  };

  const handleManualSubmit = async () => {
    const input = manualInput.trim();
    if (!input) return toast.error("Please enter a name or email");
    setLoading(true);
    try {
      const member = await verifyMemberProfile(input);
      if (member) {
        const t = await logVisit(member.name, "Manual", "Member", input, "", member.expired);
        setModalContent({ title: member.expired ? "Access Denied" : "Check-in Successful", name: member.name, time: t, type: "Member", variant: member.expired ? 'danger' : 'success', subtext: member.expired ? `Member Expired: ${member.expiryDate}` : "" });
        if (!member.expired) { setLastMemberName(member.name); setLastMemberEmail(member.email); setManualInput(''); setManualMode(false); }
        setShowModal(true);
      } else { toast.error("Member not found"); }
    } finally { setLoading(false); }
  };

  const handleScan = (result) => {
    if (result && result.length > 0 && !scannedResult) {
      const raw = result[0].rawValue; setScannedResult(raw); setIsPaused(true);
      verifyMemberProfile(raw).then(member => {
        if (member) {
          logVisit(member.name, "QR Scan", "Member", raw, "", member.expired).then(t => {
            setModalContent({ title: member.expired ? "Access Denied" : "Check-in Successful", name: member.name, time: t, type: "Member", variant: member.expired ? 'danger' : 'success', subtext: member.expired ? `Member Expired: ${member.expiryDate}` : "" });
            if (!member.expired) setLastMemberName(member.name);
            setLastMemberEmail(member.email); setShowModal(true);
          });
        } else {
          setModalContent({ title: "Access Denied", name: "Invalid QR", time: "", type: "Non-Member", variant: 'danger', subtext: '' });
          setShowModal(true);
        }
      });
    }
  };

  const getModalTopBarColor = (variant) => {
    if (variant === 'success') return 'var(--brand-primary)';
    if (variant === 'danger') return 'var(--brand-secondary)';
    return 'var(--brand-info)';
  };

  return (
    <div className="qr-scanner-page">
      {!isAdminAuth ? (
        <div className="p-4 qr-modal-content shadow-lg mt-5 mx-auto" style={{ maxWidth: '420px' }}>
          <div className="qr-modal-body-container text-center">
            <h4 className="text-white mb-4 fw-bold text-uppercase text-start" style={{letterSpacing:'1px', fontSize:'14px'}}>Station Setup</h4>
            <Form.Control type="password" placeholder="Passcode" value={passcodeAttempt} onChange={(e) => setPasscodeAttempt(e.target.value)} className="qr-input mb-4 text-center" onKeyPress={(e) => e.key === 'Enter' && handlePasscodeSubmit()} />
            <button className="qr-btn qr-btn-primary w-100" onClick={handlePasscodeSubmit}>UNLOCK STATION</button>
          </div>
        </div>
      ) : (
        <>
          {!manualMode ? (
            <>
              <div className="mb-3 mt-2"><h5 className="qr-header-info">READY TO SCAN</h5><small className="qr-subtext">Place QR code or ID card in front of camera</small></div>
              <div className="qr-video-container"><Scanner onScan={handleScan} paused={isPaused} formats={['qr_code', 'pdf417']} /></div>
                <br></br>
              <Row className="g-2 mb-4 align-items-center">
                <Col xs={8}><button className="qr-btn qr-btn-warning w-100" onClick={() => setManualMode(true)}>Manual Sign In</button></Col>
                <Col xs={2} className="d-flex justify-content-center">
                  <button onClick={() => setHistoryMode(!historyMode)} className={`qr-icon-btn ${historyMode ? 'active' : ''}`}><FaFilter size={18} /></button>
                </Col>
                <Col xs={2} className="d-flex justify-content-end">
                  <button className="qr-icon-btn" style={{ color: 'var(--brand-danger)' }} onClick={handleLockStation} title="Lock Station">🔒</button>
                </Col>
              </Row>

              {historyMode && (
                <div className="qr-table-container p-3 mb-3 text-start">
                  <Row className="g-2 align-items-end">
                    <Col xs={12} md={4}><label className="qr-label">Start</label><Form.Control type="date" size="sm" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="qr-input" /></Col>
                    <Col xs={12} md={4}><label className="qr-label">End</label><Form.Control type="date" size="sm" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="qr-input" /></Col>
                    <Col xs={6} md={2}><button className="qr-btn qr-btn-primary w-100 py-1" style={{ fontSize: '0.8rem' }} onClick={() => fetchLogs(false)}>GO</button></Col>
                    <Col xs={6} md={2}><button className="qr-btn qr-btn-warning w-100 py-1" style={{ fontSize: '0.8rem' }} onClick={downloadCSV}><FaDownload size={18} /></button></Col>
                  </Row>
                </div>
              )}

              <div className="text-start">
                <h6 className="qr-subtext fw-bold mb-3 small" style={{letterSpacing:'1px'}}>{historyMode ? "LOG HISTORY" : "TODAY'S ACTIVITY"}</h6>
                
                <InputGroup className="mb-2 shadow-sm">
                    <Form.Control placeholder="🔎 Filter names..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className=" glass-search" />
                </InputGroup>
                
                <div className="d-flex gap-2 mb-3 flex-wrap">
                    <button className={`btn-sort-pill ${sortConfig.key === 'fullName' ? 'active' : ''}`} onClick={() => toggleSort('fullName')}>
                        <FaSortAlphaDown className="me-1" size={10} /> Name {sortConfig.key === 'fullName' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </button>
                    <button className={`btn-sort-pill ${sortConfig.key === 'entryType' ? 'active' : ''}`} onClick={() => toggleSort('entryType')}>
                        <FaUserFriends className="me-1" size={10} /> Type {sortConfig.key === 'entryType' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </button>
                    <button className={`btn-sort-pill ${sortConfig.key === 'scannedAt' ? 'active' : ''}`} onClick={() => toggleSort('scannedAt')}>
                        <FaRegClock className="me-1" size={10} /> Time {sortConfig.key === 'scannedAt' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </button>
                </div>

                {/* MAIN LIST CARD VIEW */}
                <div className="qr-card-list">
                  {loadingLogs ? (
                    <div className="text-center py-4 qr-subtext">Updating...</div>
                  ) : filteredLogs.length > 0 ? (
                    filteredLogs.map((log) => (
                      <div 
                        key={log.id} 
                        onClick={() => { setSelectedLog(log); setShowDetailModal(true); }}
                        className={`qr-log-card ${log.status === 'cancelled' ? 'opacity-50' : ''}`}
                      >
                        <div className="qr-card-content">
                          <div className="qr-card-main-info">
                            <div className="qr-card-name fw-bold">{formatDisplayValue(log.fullName)}</div>
                            <div className="qr-card-meta">
                              <span className={`qr-badge ${log.entryType === 'Guest' ? 'qr-badge-guest' : (log.isExpired ? 'qr-badge-expired' : 'qr-badge-member')}`}>
                                {log.entryType}
                              </span>
                              {log.signedInBy && <span className="ms-2 qr-subtext-small">Sponsor: {formatDisplayValue(log.signedInBy)}</span>}
                            </div>
                          </div>
                          
                          <div className="qr-card-time-wrap">
                            <div className="qr-time-display fw-bold">
                              {log.status === 'cancelled' && <FaBan className="text-danger me-1" size={10} />}
                              {log.scannedAt?.toDate ? log.scannedAt.toDate().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '--'}
                            </div>
                            <div className="qr-date-display small">
                              {log.scannedAt?.toDate ? log.scannedAt.toDate().toLocaleDateString([], { month: 'short', day: 'numeric' }) : ''}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-4 qr-subtext">No records found</div>
                  )}
                </div>
              </div>
            </>
          ) : (
            <div className="qr-table-container shadow-lg mt-3 p-4">
              <div className="d-flex justify-content-between align-items-center mb-4">
                <h4 className="text-white fw-bold m-0 text-uppercase">Manual Sign In</h4>
                <button className="qr-icon-btn" style={{ color: 'var(--brand-danger)' }} onClick={handleLockStation}>🔒</button>
              </div>
              <Form.Group className="mb-3 text-start"><label className="qr-label">MEMBER NAME OR EMAIL</label><Form.Control type="text" value={manualInput} onChange={(e) => setManualInput(e.target.value)} className="qr-input" /></Form.Group>
              <div className="d-grid gap-2 mt-4">
                <button className="qr-btn qr-btn-primary" onClick={handleManualSubmit} disabled={loading}>{loading ? <Spinner size="sm" /> : "VERIFY MEMBER"}</button>
                <button className="qr-btn qr-btn-warning mt-2" onClick={() => { setManualInput(''); setManualMode(false); }}>RETURN</button>
              </div>
            </div>
          )}
        </>
      )}

      {/* --- RESULT MODAL --- */}
      <Modal show={showModal} onHide={handleCloseModal} centered  contentClassName="qr-modal-content"  backdropClassName='bg-dark'>
        <div className="qr-modal-success-bar" style={{ background: getModalTopBarColor(modalContent.variant) }}></div>
        <Modal.Body className="p-0 text-center">
          <div className="qr-modal-body-container">
            <div className="mb-2" style={{ fontSize: '3.5rem', color: getModalTopBarColor(modalContent.variant) }}>
              {modalContent.variant === 'danger' ? <FaTimesCircle /> : <FaCheckCircle />}
            </div>
            <h2 className="mb-1 fw-bold">{formatDisplayValue(modalContent.name)}</h2>
            <div className={`qr-badge mb-3 d-inline-block px-4 py-2 ${modalContent.variant === 'danger' ? 'qr-badge-expired' : 'qr-badge-member'}`}>{modalContent.variant === 'danger' ? 'EXPIRED' : modalContent.type}</div>
            
            {modalContent.subtext && <div className="p-3 mb-3 rounded qr-subtext-warning" style={{background:'rgba(255,193,7,0.1)', border:'1px solid rgba(255,193,7,0.2)'}}><h6 className="m-0 small fw-bold text-warning">{modalContent.subtext}</h6></div>}
            
            {modalContent.variant === 'success' && (
              <div className="qr-guest-section border-top border-secondary pt-3 mt-3">
                <label className="qr-label mb-2 text-start d-block">Bringing a guest?</label>
                <InputGroup className="mb-2">
                  <Form.Control placeholder="Guest Name..." className="qr-input" value={guestNameInput} onChange={(e) => setGuestNameInput(e.target.value)} onKeyPress={(e) => e.key === 'Enter' && handleQuickGuestAdd()} disabled={addingGuest}/>
                  <button className="qr-btn qr-btn-primary px-3" onClick={handleQuickGuestAdd} disabled={addingGuest}>
                    {addingGuest ? <Spinner animation="border" size="sm" /> : <FaUserPlus size={18} />}
                  </button>
                </InputGroup>
              </div>
            )}
            <button className="qr-btn qr-btn-warning qr-btn-finish w-100 mt-3" onClick={handleCloseModal}>Finish</button>
          </div>
        </Modal.Body>
      </Modal>

      {/* --- GUEST MANAGEMENT MODAL --- */}
      <Modal show={showGuestModal} onHide={() => setShowGuestModal(false)}  centered contentClassName="qr-modal-content" backdropClassName='bg-dark'>
        <div className="qr-modal-success-bar" style={{ background: 'var(--brand-primary)' }}></div>
        <Modal.Body className="p-0">
          <div className="qr-modal-body-container">
            <div className="mb-4 text-center">
              <div className="mb-2" style={{ fontSize: '2.5rem', color: 'var(--brand-primary)' }}><FaUserPlus /></div>
              <h2 className="fw-bold text-white mb-1">Guest Management</h2>
              <p className="qr-subtext small">Sponsor: {formatDisplayValue(lastMemberName)}</p>
            </div>
            <div className="qr-guest-section mb-4">
              <label className="qr-label mb-2 text-start d-block small fw-bold">ADD NEW GUEST</label>
              <InputGroup>
                <Form.Control 
                  placeholder="Guest Full Name..." 
                  className="qr-input" 
                  value={guestNameInput} 
                  onChange={(e) => setGuestNameInput(e.target.value)} 
                  onKeyPress={(e) => e.key === 'Enter' && handleQuickGuestAdd()} 
                  disabled={addingGuest}
                />
                <button className="qr-btn qr-btn-primary px-3" onClick={handleQuickGuestAdd} disabled={addingGuest}>
                  {addingGuest ? <Spinner animation="border" size="sm" /> : "ADD"}
                </button>
              </InputGroup>
            </div>
            
            <div className="qr-card-list" style={{ maxHeight: '250px', overflowY: 'auto' }}>
              {guestItems.map((guest) => (
                <div key={guest.id} className="qr-log-card py-2">
                  <div className="d-flex justify-content-between align-items-center">
                    <div className="fw-bold text-white small">{formatDisplayValue(guest.fullName)}</div>
                    <button 
                      className="btn btn-sm text-danger border-0 fw-bold p-0 d-flex align-items-center" 
                      onClick={() => handleCancelGuest(guest.id, lastMemberEmail)}
                      disabled={removingGuestId === guest.id}
                    >
                      {removingGuestId === guest.id ? (
                        <Spinner animation="border" size="sm" className="me-1" />
                      ) : (
                        "REMOVE"
                      )}
                    </button>
                  </div>
                </div>
              ))}
              {guestItems.length === 0 && <div className="text-center py-4 qr-subtext">No guests found</div>}
            </div>
            <button className="qr-btn qr-btn-warning w-100 mt-4" onClick={() => setShowGuestModal(false)}>Close</button>
          </div>
        </Modal.Body>
      </Modal>

      {/* --- ENTRY DETAILS MODAL --- */}
      <Modal show={showDetailModal} onHide={() => setShowDetailModal(false)}  centered contentClassName="qr-modal-content" backdropClassName='bg-dark'>
        <div className="qr-modal-success-bar" style={{ background: selectedLog?.status === 'cancelled' ? 'var(--brand-secondary)' : 'var(--brand-info)' }}></div>
        <Modal.Body className="p-0">
          <div className="qr-modal-body-container text-center">
            <div className="mb-3" style={{ fontSize: '2.5rem', color: 'var(--brand-info)' }}><FaInfoCircle /></div>
            {selectedLog && (
              <div className="qr-details-container text-start text-white">
                <h3 className="fw-bold mb-4 text-center">{formatDisplayValue(selectedLog.fullName)}</h3>
                <Row className="mb-2"><Col xs={5} className="qr-label-detail small text-uppercase fw-bold">Type:</Col><Col className="small">{selectedLog.entryType}</Col></Row>
                <Row className="mb-2"><Col xs={5} className="qr-label-detail small text-uppercase fw-bold">Method:</Col><Col className="small">{selectedLog.method}</Col></Row>
                {selectedLog.signedInBy && (<Row className="mb-2"><Col xs={5} className="qr-label-detail small text-uppercase fw-bold">Sponsor:</Col><Col className="text-warning small">{formatDisplayValue(selectedLog.signedInBy)}</Col></Row>)}
                <Row className="mb-2"><Col xs={5} className="qr-label-detail small text-uppercase fw-bold">Status:</Col><Col className={selectedLog.status === 'cancelled' ? "text-danger small fw-bold" : "text-success small"}>{selectedLog.status?.toUpperCase() || "SUCCESS"}</Col></Row>
                <Row className="mb-2"><Col xs={5} className="qr-label-detail small text-uppercase fw-bold">Time:</Col><Col className="qr-subtext small">{selectedLog.scannedAt?.toDate().toLocaleString()}</Col></Row>
                
                {selectedLog.entryType === 'Member' && !selectedLog.isExpired && selectedLog.status !== 'cancelled' && (
                   <button className="qr-btn qr-btn-primary w-100 mt-3 py-2" onClick={() => { 
                       setLastMemberEmail(selectedLog.rawPayload); 
                       setLastMemberName(selectedLog.fullName); 
                       fetchGuestsForMember(selectedLog.rawPayload); 
                       setShowGuestModal(true); 
                    }}>MANAGE GUESTS</button>
                )}
              </div>
            )}
            <button className="qr-btn qr-btn-warning w-100 mt-3" onClick={() => setShowDetailModal(false)}>Close</button>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default QRWebAndNativeScan;